/**
 * Incoming Call Notification Component
 * Shows a popup when there's an incoming call
 */

import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Phone, PhoneOff, User, Video } from 'lucide-react';

export function IncomingCallNotification({
  isOpen,
  callerName,
  callerPhone,
  callType = 'video',
  onAccept,
  onDecline,
  autoDeclineSeconds = 60
}) {
  const [countdown, setCountdown] = useState(autoDeclineSeconds);

  // Auto-decline countdown
  useEffect(() => {
    if (!isOpen) {
      setCountdown(autoDeclineSeconds);
      return;
    }

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          onDecline?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, autoDeclineSeconds, onDecline]);

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-md bg-gradient-to-b from-gray-900 to-gray-800 border-gray-700"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="flex flex-col items-center py-6" data-testid="incoming-call-notification">
          {/* Caller Avatar with pulse animation */}
          <div className="relative mb-6">
            <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center">
              <User className="h-12 w-12 text-primary" />
            </div>
            <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
          </div>

          {/* Caller Info */}
          <h2 className="text-xl font-semibold text-white mb-1">
            {callerName || 'Unknown Caller'}
          </h2>
          {callerPhone && (
            <p className="text-gray-400 text-sm mb-2">{callerPhone}</p>
          )}
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-6">
            {callType === 'video' ? (
              <Video className="h-4 w-4" />
            ) : (
              <Phone className="h-4 w-4" />
            )}
            <span>Incoming {callType} call</span>
          </div>

          {/* Countdown */}
          <p className="text-gray-500 text-xs mb-6">
            Auto-decline in {countdown}s
          </p>

          {/* Action Buttons */}
          <div className="flex items-center gap-8">
            {/* Decline */}
            <div className="flex flex-col items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={onDecline}
                className="h-16 w-16 rounded-full bg-red-500/20 text-red-500 hover:bg-red-500 hover:text-white transition-all"
                data-testid="decline-call-btn"
              >
                <PhoneOff className="h-7 w-7" />
              </Button>
              <span className="text-gray-400 text-xs">Decline</span>
            </div>

            {/* Accept */}
            <div className="flex flex-col items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={onAccept}
                className="h-16 w-16 rounded-full bg-green-500/20 text-green-500 hover:bg-green-500 hover:text-white transition-all animate-pulse"
                data-testid="accept-call-btn"
              >
                <Phone className="h-7 w-7" />
              </Button>
              <span className="text-gray-400 text-xs">Accept</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default IncomingCallNotification;
